import React from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronLeft, Check, CircleHelp, Compass, RotateCcw, TrainFront } from "lucide-react";
import {
  ApiError,
  angularDifference,
  compass16,
  compass8Long,
  insideLobe,
  LOOP_DEFAULT_KM,
  LOOP_MAX_KM,
  LOOP_MIN_KM,
  LOOP_PRESETS,
  type StartSummary,
  type WhereToStartHill,
  type WhereToStartMode,
  type WhereToStartParams,
  type WhereToStartSearchResult,
  type WhereToStartStartType,
} from "@atlas/shared";
import {
  fetchWtsLists,
  fetchWtsRank,
  fetchWtsRemaining,
  fetchWtsStart,
} from "../api/where-to-start";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { Skel } from "../components/Skeleton";
import { WhereToStartIntro } from "../components/WhereToStartIntro";
import { WhereToStartListPicker } from "../components/WhereToStartListPicker";
import { WhereToStartSearch } from "../components/WhereToStartSearch";
import { AREA_LABEL, boundsOf, DEFAULT_RADIUS_KM, haversineM, reachBounds, WhereToStartMap, type Bounds, type WtsOrigin, type WtsPicked } from "../components/map/WhereToStartMap";
import { useIsPro } from "../hooks/useIsPro";
import { usePageMeta } from "../hooks/usePageMeta";
import type { WhereToStartSearchParams } from "../router";

// Where to start (spec 31 / ATL-146). Rank stations and car parks by the
// hills left on the chosen lists, and show the best side of a start as an
// area the user can swing round it. Atlas never orders hills, draws lines
// between them or suggests a loop (DECISIONS.md, positioning).

const STORAGE_KEY = "atlas-where-to-start";
const INTRO_KEY = "atlas-where-to-start-intro-seen";
// Fallback when storage is unavailable: show once per page load, never loop.
let introShownThisLoad = false;
const PRESET_ORDER = ["short", "day", "long"] as const;
const PRESET_LABEL = { short: "Short", day: "Day", long: "Long" } as const;

interface Stored {
  lists?: string[];
  loop?: number;
  mode?: WhereToStartMode;
}

function readStored(): Stored {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const v = JSON.parse(raw) as Stored;
    return {
      lists: Array.isArray(v.lists) ? v.lists.filter((s) => typeof s === "string") : undefined,
      loop: typeof v.loop === "number" ? v.loop : undefined,
      mode: v.mode === "centred" || v.mode === "lobe" ? v.mode : undefined,
    };
  } catch {
    return {};
  }
}

function writeStored(patch: Stored) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readStored(), ...patch }));
  } catch {
    /* storage unavailable: the page still works */
  }
}

const clampLoop = (n: number) => Math.min(LOOP_MAX_KM, Math.max(LOOP_MIN_KM, Math.round(n)));
const fmtKm = (n: number) => `${Math.round(n * 10) / 10}`;
const km = (m: number) => `${Math.round((m / 1000) * 10) / 10}`;
const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** D27: "and 19 other car parks in Buxton" under a grouped start. */
function alternativesLine(s: StartSummary): string | null {
  const n = s.alternativeCount;
  if (n <= 0) return null;
  const noun = s.kind === "station" ? plural(n, "station", "stations") : plural(n, "car park", "car parks");
  return `and ${n} other ${noun}${s.placeName ? ` in ${s.placeName}` : " nearby"}`;
}

/** Short list codes for tags: first letter, two letters when letters collide. */
function listCodes(names: string[]): string[] {
  const one = names.map((n) => n.trim().charAt(0).toUpperCase());
  return names.map((n, i) => {
    const clash = one.some((c, j) => j !== i && c === one[i]);
    return clash ? n.trim().slice(0, 2).replace(/^./, (c) => c.toUpperCase()) : (one[i] ?? "");
  });
}

const CSS = `
.wts-root{flex:1;min-height:0;min-width:0;display:flex;background:var(--bg-app);color:var(--text-primary)}
.wts-panel{flex:0 0 380px;max-width:100%;box-sizing:border-box;overflow-y:auto;background:var(--bg-base);border-right:1px solid var(--border-default);display:flex;flex-direction:column}
.wts-map{flex:999 1 560px;min-width:0;position:relative;overflow:hidden}
.wts-lc{display:inline-flex;align-items:center;gap:6px;height:34px;padding:0 12px;border-radius:999px;border:1px solid var(--border-default);background:var(--surface-sunken);color:var(--text-secondary);font:600 13px var(--font-sans);cursor:pointer}
.wts-lc:hover{background:var(--surface-card);color:var(--text-primary)}
.wts-lc[aria-pressed="true"]{background:var(--accent-soft);border-color:var(--border-gold);color:var(--text-accent)}
.wts-lc.dashed{border-style:dashed}
.wts-seg{flex:1;height:34px;border:0;background:none;border-radius:8px;color:var(--text-secondary);font:600 13px var(--font-sans);cursor:pointer}
.wts-seg:hover{color:var(--text-primary)}
.wts-seg[aria-pressed="true"]{background:var(--surface-hover);color:var(--text-primary)}
.wts-row{display:flex;align-items:center;gap:12px;width:100%;padding:10px 12px;min-height:44px;border:0;border-radius:12px;background:transparent;color:var(--text-primary);text-align:left;cursor:pointer;font-family:var(--font-sans)}
.wts-row:hover{background:var(--surface-raised)}
.wts-lc:focus-visible,.wts-seg:focus-visible,.wts-row:focus-visible,.wts-link:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
.wts-field:focus-within{border-color:var(--border-gold);box-shadow:var(--focus-shadow)}
.wts-field input:focus-visible,.wts-field input:focus{outline:none;box-shadow:none}
.wts-link{color:var(--text-accent);text-decoration:none;font-weight:600;background:none;border:0;padding:0;cursor:pointer;font-family:var(--font-sans);font-size:inherit}
.wts-link:hover{text-decoration:underline}
.wts-hill{display:flex;align-items:center;gap:10px;min-height:36px;font-size:14px}
.wts-alts{margin-top:14px;padding-top:10px;border-top:1px solid var(--border-subtle)}
.wts-alts>summary{cursor:pointer;font-size:12px;color:var(--text-muted);padding:8px 0}
.wts-alts>summary:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
.wts-tag{font:700 10px var(--font-mono);letter-spacing:.06em;color:var(--text-secondary);border:1px solid var(--border-strong);border-radius:5px;padding:1px 5px;white-space:nowrap}
@media (max-width:760px){
  .wts-root{flex-direction:column;overflow-y:auto}
  .wts-map{order:-1;flex:none;height:46vh;min-height:300px}
  .wts-panel{flex:none;overflow:visible;width:100%;border-right:0}
  .wts-bub .cap{display:none}
  .wts-bub .pill{height:28px;padding:0 9px;font-size:13px}
  .wts-legend-hint{display:none}
}
`;

// --- small controls ---------------------------------------------------------

function StepLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="eyebrow" style={{ fontSize: 10 }}>
      {children}
    </span>
  );
}

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      style={{
        display: "flex",
        gap: 2,
        padding: 3,
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-subtle)",
        borderRadius: 10,
      }}
    >
      {options.map((o) => (
        <button key={o.value} type="button" className="wts-seg" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function StartIcon({ kind, size = 32 }: { kind: StartSummary["kind"]; size?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: 8,
        flex: "none",
        display: "grid",
        placeItems: "center",
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-default)",
        color: "var(--text-secondary)",
        font: "700 14px var(--font-display)",
      }}
    >
      {kind === "station" ? <TrainFront size={16} /> : "P"}
    </span>
  );
}

// --- page --------------------------------------------------------------------

export default function WhereToStartPage() {
  usePageMeta({
    title: "Where to start",
    description: "Rank stations and car parks by the hills left on your lists.",
    canonicalPath: "/where-to-start",
    noindex: true,
  });

  const search = useSearch({ strict: false }) as WhereToStartSearchParams;
  const navigate = useNavigate();
  const { isPro } = useIsPro();
  const stored = React.useMemo(readStored, []);

  const update = React.useCallback(
    (patch: Partial<WhereToStartSearchParams>) => {
      void navigate({
        to: "/where-to-start",
        search: ((prev: WhereToStartSearchParams) => {
          const next: Record<string, unknown> = { ...prev, ...patch };
          for (const k of Object.keys(next)) if (next[k] === undefined) delete next[k];
          return next as WhereToStartSearchParams;
        }) as never,
        replace: true,
      });
    },
    [navigate],
  );

  // --- lists ---
  const listsQ = useQuery({ queryKey: ["where-to-start/lists"], queryFn: fetchWtsLists, staleTime: 60_000 });
  const allLists = React.useMemo(() => listsQ.data?.lists ?? [], [listsQ.data]);
  // First visit default: the one started list the user is furthest through
  // (highest collected share) that still has hills left; ties go to the list
  // with more hills collected. Nothing started = nothing ticked.
  const startedSlugs = React.useMemo(() => {
    const open = allLists.filter((l) => l.started && l.remaining > 0 && l.total > 0);
    if (open.length === 0) return [];
    const share = (l: (typeof open)[number]) => (l.total - l.remaining) / l.total;
    const best = open.reduce((a, b) =>
      share(b) > share(a) || (share(b) === share(a) && b.total - b.remaining > a.total - a.remaining) ? b : a,
    );
    return [best.slug];
  }, [allLists]);

  const selected = React.useMemo(() => {
    const requested = search.lists !== undefined ? search.lists.split(",").filter(Boolean) : (stored.lists ?? null);
    if (!listsQ.data) return (requested ?? []).slice(0, 20);
    const known = new Set(allLists.map((l) => l.slug));
    let slugs = requested ? requested.filter((x) => known.has(x)) : startedSlugs;
    // Every remembered slug is stale (a list was retired): start again.
    if (requested && requested.length > 0 && slugs.length === 0) slugs = startedSlugs;
    // Keep the order the server uses; the first one is "the top list".
    const order = new Map(allLists.map((l, i) => [l.slug, i]));
    return [...slugs].sort((x, y) => (order.get(x) ?? 0) - (order.get(y) ?? 0)).slice(0, 20);
  }, [search.lists, stored.lists, listsQ.data, allLists, startedSlugs]);
  const listsKey = selected.join(",");
  const listsReady = listsQ.data != null || search.lists !== undefined || stored.lists != null;

  const toggleList = (slug: string) => {
    const next = selected.includes(slug) ? selected.filter((s) => s !== slug) : [...selected, slug];
    writeStored({ lists: next });
    update({ lists: next.length > 0 ? next.join(",") : "", bearing: undefined });
  };

  // --- loop, mode, start type, origin ---
  const loop = clampLoop(search.loop ?? stored.loop ?? LOOP_DEFAULT_KM);
  const mode: WhereToStartMode = search.mode ?? stored.mode ?? "lobe";
  const startType: WhereToStartStartType = search.type ?? "any";
  const radiusKm = Math.min(30, Math.max(2, search.r ?? DEFAULT_RADIUS_KM));
  const origin: WtsOrigin | null =
    search.lat != null && search.lng != null ? { lat: search.lat, lng: search.lng, radiusKm } : null;
  const place = search.place ?? (origin ? "Near you" : null);
  const startId = search.start ?? null;

  const params: WhereToStartParams = React.useMemo(
    () => ({
      lists: selected,
      loop,
      mode,
      startType,
      ...(origin ? { lat: origin.lat, lng: origin.lng, radius: origin.radiusKm } : {}),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listsKey, loop, mode, startType, origin?.lat, origin?.lng, origin?.radiusKm],
  );

  const haveLists = selected.length > 0;
  const remainingQ = useQuery({
    queryKey: ["where-to-start/remaining", listsKey],
    queryFn: () => fetchWtsRemaining(selected),
    enabled: haveLists,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
  const rankQ = useQuery({
    queryKey: ["where-to-start/rank", params],
    queryFn: () => fetchWtsRank(params),
    enabled: haveLists,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
  const rank = haveLists ? rankQ.data : undefined;
  const showTypeControl = isPro || rank?.gated === false;

  // A free account is only ever served its single best start.
  // An equivalent start folded into the best one (D27) is the same place.
  const bestStart = rank?.starts[0];
  const startGated =
    startId != null &&
    rank?.gated === true &&
    bestStart?.id !== startId &&
    !(bestStart?.alternatives ?? []).some((a) => a.id === startId);
  const detailEnabled = startId != null && haveLists && (isPro || rank != null) && !startGated;

  // Bearing: live while dragging, committed to the URL shortly after.
  const [liveBearing, setLiveBearing] = React.useState<number | null>(null);
  const commitTimer = React.useRef<number | undefined>(undefined);
  // Any change that re-ranks (start, shape, lists, loop) drops the dragged
  // bearing, as the URL's does, so the lobe follows the new best side.
  React.useEffect(() => {
    setLiveBearing(null);
    return () => window.clearTimeout(commitTimer.current);
  }, [startId, mode, listsKey, loop]);
  const onBearingChange = React.useCallback((b: number) => setLiveBearing(b), []);
  const onBearingCommit = React.useCallback(
    (b: number) => {
      setLiveBearing(b);
      window.clearTimeout(commitTimer.current);
      commitTimer.current = window.setTimeout(() => update({ bearing: Math.round(b * 10) / 10 }), 300);
    },
    [update],
  );

  const detailQ = useQuery({
    queryKey: ["where-to-start/starts", startId, { ...params, bearing: search.bearing }],
    queryFn: () => fetchWtsStart(startId as string, { ...params, bearing: search.bearing }),
    enabled: detailEnabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    retry: false,
  });
  const detail = detailQ.data && detailQ.data.start.id === startId ? detailQ.data : undefined;
  const detailGated = startGated || detail?.gated === true;

  // --- derived picked-start data (all recounted locally from the hills) ---
  const radiusM = detail?.radiusM ?? 0;
  const detailMode = detail?.mode ?? mode;
  const bearing: number | null =
    detailMode === "centred" ? null : (liveBearing ?? search.bearing ?? detail?.bearing ?? detail?.best?.bearing ?? 0);

  const hills: WhereToStartHill[] = React.useMemo(() => {
    if (!detail || detail.gated) return [];
    if (detailMode !== "lobe" || bearing == null) return detail.hills;
    return detail.hills.map((h) => ({ ...h, inside: insideLobe(h.distanceM, h.bearing, detail.radiusM, bearing) }));
  }, [detail, detailMode, bearing]);
  const insideHills = React.useMemo(() => hills.filter((h) => h.inside), [hills]);

  const picked: WtsPicked | null = React.useMemo(() => {
    if (!detail || detail.gated) return null;
    return {
      start: detail.start,
      radiusM: detail.radiusM,
      mode: detail.mode,
      bearing,
      bestBearing: detail.best?.bearing ?? null,
      runnerUpBearing: detail.runnerUp?.bearing ?? null,
      hills,
      insideCount: insideHills.length,
    };
  }, [detail, bearing, hills, insideHills.length]);

  const pickStart = React.useCallback((id: string) => update({ start: id, bearing: undefined }), [update]);

  // --- map fit ---
  // Bumped on every origin pick so re-picking the same place still re-frames.
  const [focusNonce, setFocusNonce] = React.useState(0);
  const fit = React.useMemo<{ key: string; bounds: Bounds | null }>(() => {
    const summary = detail?.start ?? rank?.starts.find((s) => s.id === startId);
    if (startId && summary && !startGated) {
      const r = detail?.radiusM ?? rank?.radiusM ?? 0;
      const reach = (detail?.mode ?? rank?.mode ?? mode) === "lobe" ? 2 * r : r;
      return { key: `p:${startId}:${loop}:${mode}`, bounds: reachBounds(summary.lat, summary.lng, reach) };
    }
    // An origin is a real focus: always frame the berth round it.
    if (origin) {
      // "Search this area" keeps the camera the user framed.
      if (place === AREA_LABEL) return { key: `a:${origin.lat}:${origin.lng}`, bounds: null };
      return { key: `o:${origin.lat}:${origin.lng}:${focusNonce}`, bounds: reachBounds(origin.lat, origin.lng, origin.radiusKm * 1000) };
    }
    if (rank && rank.starts.length >= 2) {
      const pts = rank.starts.map((s) => [s.lng, s.lat] as [number, number]);
      return { key: `s:${listsKey}:${startType}`, bounds: boundsOf(pts) };
    }
    const pts = (remainingQ.data?.peaks ?? []).filter((p) => !p.collected).map((p) => [p.lng, p.lat] as [number, number]);
    return { key: `r:${listsKey}`, bounds: boundsOf(pts) };
  }, [detail, rank, startId, startGated, loop, mode, listsKey, origin, startType, remainingQ.data, focusNonce]);

  // --- near me ---
  const [geoMsg, setGeoMsg] = React.useState<string | null>(null);
  const [geoBusy, setGeoBusy] = React.useState(false);
  const nearMe = () => {
    if (!("geolocation" in navigator)) {
      setGeoMsg("Your browser can't share a location, so this stays on Anywhere.");
      return;
    }
    setGeoBusy(true);
    setGeoMsg(null);
    setFocusNonce((n) => n + 1);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoBusy(false);
        update({
          lat: Math.round(pos.coords.latitude * 1000) / 1000,
          lng: Math.round(pos.coords.longitude * 1000) / 1000,
          place: "Near you",
          r: undefined,
          start: undefined,
          bearing: undefined,
        });
      },
      () => {
        setGeoBusy(false);
        setGeoMsg("Couldn't get your location, so this stays on Anywhere.");
      },
      { timeout: 10_000, maximumAge: 10 * 60_000 },
    );
  };
  const pickPlace = (r: WhereToStartSearchResult) => {
    setGeoMsg(null);
    setFocusNonce((n) => n + 1);
    const base = { lat: Math.round(r.lat * 1000) / 1000, lng: Math.round(r.lng * 1000) / 1000, place: r.name, r: undefined, bearing: undefined };
    if ((r.kind === "station" || r.kind === "car_park") && r.id) update({ ...base, start: r.id });
    else update({ ...base, start: undefined });
  };
  const searchArea = React.useCallback(
    (a: { lat: number; lng: number; radiusKm: number }) => {
      setGeoMsg(null);
      update({
        lat: a.lat,
        lng: a.lng,
        place: AREA_LABEL,
        r: a.radiusKm === DEFAULT_RADIUS_KM ? undefined : a.radiusKm,
        start: undefined,
        bearing: undefined,
      });
    },
    [update],
  );
  const clearPlace = () => update({ lat: undefined, lng: undefined, place: undefined, r: undefined, start: undefined, bearing: undefined });

  // --- loop control ---
  const presetOf = (PRESET_ORDER.find((p) => LOOP_PRESETS[p] === loop) ?? null) as keyof typeof LOOP_PRESETS | null;
  const [customOpen, setCustomOpen] = React.useState(presetOf == null);
  const [sliderKm, setSliderKm] = React.useState(loop);
  React.useEffect(() => setSliderKm(loop), [loop]);
  const sliderTimer = React.useRef<number | undefined>(undefined);
  React.useEffect(() => () => window.clearTimeout(sliderTimer.current), []);
  const setLoop = (n: number) => {
    const v = clampLoop(n);
    writeStored({ loop: v });
    update({ loop: v, bearing: undefined });
  };
  const dayValue = customOpen || presetOf == null ? "custom" : presetOf;
  const dayLabel = presetOf && !customOpen ? PRESET_LABEL[presetOf] : "Custom";

  const setMode = (m: WhereToStartMode) => {
    writeStored({ mode: m });
    update({ mode: m, bearing: undefined });
  };

  // --- shared blocks ---
  const listsBlock = (compact: boolean) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {!compact && <StepLabel>1 · Lists that count</StepLabel>}
      {listsQ.isLoading ? (
        <Skel height={44} radius="10px" />
      ) : listsQ.isError ? (
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
          Couldn't load your lists.{" "}
          <button type="button" className="wts-link" onClick={() => void listsQ.refetch()}>
            Try again
          </button>
        </div>
      ) : (
        <WhereToStartListPicker lists={allLists} selected={selected} onToggle={toggleList} />
      )}
      {!compact && startedSlugs.length > 0 && search.lists === undefined && (
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Starts with the list you're furthest through. Add more.</span>
      )}
    </div>
  );

  const dayBlock = (compact: boolean) => {
    const shown = customOpen || presetOf == null ? sliderKm : loop;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {!compact && <StepLabel>3 · How big a day</StepLabel>}
        <Segmented
          label="How big a day"
          value={dayValue}
          options={[
            ...PRESET_ORDER.map((p) => ({ value: p as string, label: PRESET_LABEL[p] })),
            { value: "custom", label: "Custom" },
          ]}
          onChange={(v) => {
            if (v === "custom") {
              setCustomOpen(true);
              return;
            }
            setCustomOpen(false);
            setLoop(LOOP_PRESETS[v as keyof typeof LOOP_PRESETS]);
          }}
        />
        {(customOpen || presetOf == null) && (
          <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "var(--text-secondary)" }}>
            <input
              type="range"
              min={LOOP_MIN_KM}
              max={LOOP_MAX_KM}
              step={1}
              value={sliderKm}
              aria-label="Loop length in kilometres"
              onChange={(e) => {
                const v = Number(e.target.value);
                setSliderKm(v);
                window.clearTimeout(sliderTimer.current);
                sliderTimer.current = window.setTimeout(() => setLoop(v), 300);
              }}
              style={{ flex: 1, minWidth: 0, accentColor: "var(--accent)" }}
            />
            <span className="tnum" style={{ minWidth: 48, textAlign: "right", fontFamily: "var(--font-mono)" }}>
              {sliderKm} km
            </span>
          </label>
        )}
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {mode === "centred"
            ? `${dayLabel}: everything within ${km(((customOpen ? shown : loop) * 1000) / 4)} km of the start.`
            : `${dayLabel}: a loop of about ${customOpen || presetOf == null ? shown : loop} km.${compact ? "" : " Each start is scored on its best side."}`}
        </span>
      </div>
    );
  };

  const rankError = rankQ.isError ? rankQ.error : null;
  const errorCopy = (e: unknown) =>
    e instanceof ApiError && e.status === 429 ? "Slow down a moment." : "Something went wrong loading starts.";

  const topListName = (slug: string | null) => (slug ? rank?.lists.find((l) => l.slug === slug)?.name : undefined);
  const startSub = (s: StartSummary) => {
    const list = topListName(s.topList);
    const where =
      rank?.mode === "centred"
        ? list
          ? `${list} within ${km(rank.radiusM)} km`
          : null
        : list && s.direction
          ? `${list} to the ${s.direction}`
          : null;
    return [s.placeName, where].filter(Boolean).join(" · ");
  };

  const startRow = (s: StartSummary, best?: boolean) => (
    <button
      key={s.id}
      type="button"
      className="wts-row"
      onClick={() => pickStart(s.id)}
      style={best ? { background: "var(--surface-card)", border: "1px solid var(--border-gold)" } : undefined}
    >
      <StartIcon kind={s.kind} />
      <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
        <span style={{ fontWeight: 600, fontSize: 15 }}>{s.name}</span>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{startSub(s)}</span>
        {alternativesLine(s) && <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{alternativesLine(s)}</span>}
      </span>
      <span
        className="tnum"
        style={{ font: "700 22px var(--font-display)", minWidth: 28, textAlign: "right", color: best ? "var(--text-accent)" : "var(--text-primary)" }}
      >
        {s.count}
      </span>
    </button>
  );

  const leftCount = React.useMemo(() => {
    const peaks = remainingQ.data?.peaks ?? [];
    return peaks.filter(
      (p) => !p.collected && (!origin || haversineM(origin.lat, origin.lng, p.lat, p.lng) <= (origin?.radiusKm ?? DEFAULT_RADIUS_KM) * 1000),
    ).length;
  }, [remainingQ.data, origin]);

  // --- intro (first visit, then on request) ---
  const [introOpen, setIntroOpen] = React.useState(false);
  React.useEffect(() => {
    if (startId || introShownThisLoad) return;
    let seen = false;
    try {
      seen = window.localStorage.getItem(INTRO_KEY) === "1";
    } catch {
      /* storage unavailable: fall back to once per page load */
    }
    if (seen) return;
    introShownThisLoad = true;
    setIntroOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const closeIntro = () => {
    setIntroOpen(false);
    try {
      window.localStorage.setItem(INTRO_KEY, "1");
    } catch {
      /* ignore */
    }
  };
  const helpButton = (
    <button
      type="button"
      aria-label="How Where to start works"
      onClick={() => setIntroOpen(true)}
      style={{ width: 32, height: 32, flex: "none", display: "grid", placeItems: "center", border: 0, borderRadius: 8, background: "transparent", color: "var(--text-secondary)", cursor: "pointer" }}
    >
      <CircleHelp size={18} aria-hidden="true" />
    </button>
  );

  // --- panel: state A ---
  const stateA = (
    <>
      <div style={{ padding: "24px 20px 18px", display: "flex", flexDirection: "column", gap: 16, borderBottom: "1px solid var(--border-subtle)" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="eyebrow">Toolkit</span>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <h1 style={{ margin: 0, font: "600 26px var(--font-display)" }}>Where to start</h1>
            {helpButton}
          </div>
        </div>
        {listsBlock(false)}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <StepLabel>2 · Where</StepLabel>
          <WhereToStartSearch place={place} onPick={pickPlace} onClear={clearPlace} onNearMe={nearMe} nearMeBusy={geoBusy} />
          <span style={{ fontSize: 12, color: geoMsg ? "var(--text-secondary)" : "var(--text-muted)" }} role={geoMsg ? "status" : undefined}>
            {geoMsg ?? "Used to measure distances, never kept."}
          </span>
        </div>
        {dayBlock(false)}
        {showTypeControl && (
          <Segmented
            label="Start type"
            value={startType}
            options={[
              { value: "any", label: "Any" },
              { value: "station", label: "Stations" },
              { value: "car_park", label: "Car parks" },
            ]}
            onChange={(t) => update({ type: t === "any" ? undefined : t, bearing: undefined })}
          />
        )}
      </div>

      <div style={{ padding: "16px 8px 8px", display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={{ padding: "0 12px 8px", display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
          <h2 style={{ margin: 0, font: "600 17px var(--font-display)" }}>Best places to start</h2>
          <span style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }}>
            {place ?? "Anywhere"}
          </span>
        </div>

        {!listsReady || (listsQ.isLoading && !haveLists) ? (
          <RowSkeletons />
        ) : !haveLists ? (
          <p style={{ margin: 0, padding: "8px 12px", fontSize: 14, color: "var(--text-secondary)" }}>Pick a list to begin.</p>
        ) : rankQ.isLoading ? (
          <RowSkeletons />
        ) : rankError ? (
          <EmptyState
            icon={<Compass size={20} />}
            title={errorCopy(rankError)}
            action={{ label: "Try again", onClick: () => void rankQ.refetch() }}
            style={{ margin: "0 4px" }}
          />
        ) : rank && rank.starts.length === 0 ? (
          <p style={{ margin: 0, padding: "8px 12px", fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5 }}>
            Nothing left on these lists within reach of a station or car park. Pick another list or a longer day.
          </p>
        ) : rank ? (
          <>
            {rank.starts.map((s, i) => startRow(s, rank.gated && i === 0))}
            {rank.gated && (
              <>
                <div aria-hidden="true" style={{ display: "flex", flexDirection: "column", gap: 6, opacity: 0.7, marginTop: 6 }}>
                  {[150, 120, 170].map((w) => (
                    <div key={w} style={{ height: 56, borderRadius: 12, background: "var(--surface-sunken)", display: "flex", alignItems: "center", gap: 12, padding: "0 12px" }}>
                      <span style={{ width: 32, height: 32, borderRadius: 8, background: "var(--surface-raised)" }} />
                      <span style={{ width: w, height: 10, borderRadius: 4, background: "var(--surface-raised)" }} />
                      <span style={{ marginLeft: "auto", width: 18, height: 18, borderRadius: 4, background: "var(--surface-raised)" }} />
                    </div>
                  ))}
                </div>
                {rank.moreCount > 0 && (
                  <div
                    style={{
                      margin: "10px 4px 0",
                      padding: 18,
                      borderRadius: 12,
                      background: "var(--surface-card)",
                      border: "1px solid var(--border-default)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 14,
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <span style={{ font: "600 16px var(--font-display)" }}>
                        {rank.moreCount} more {plural(rank.moreCount, "place", "places")} to start
                      </span>
                      <span style={{ fontSize: 14, lineHeight: 1.5, color: "var(--text-secondary)" }}>
                        Pro ranks every station and car park for your lists and shows which hills each one reaches.
                      </span>
                    </div>
                    <Button block onClick={() => void navigate({ to: "/plan" })}>
                      See all with Pro
                    </Button>
                  </div>
                )}
              </>
            )}
            {rank.bestForList.map((b) => {
              const list = rank.lists.find((l) => l.slug === b.slug);
              const n = b.start.byList.find((x) => x.slug === b.slug)?.count ?? b.start.count;
              return (
                <div
                  key={b.slug}
                  style={{ margin: "10px 4px 0", padding: 12, borderRadius: 12, background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)", display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <span className="eyebrow" style={{ fontSize: 10 }}>
                    Best for {list?.name ?? b.slug}
                  </span>
                  <button
                    type="button"
                    className="wts-row"
                    onClick={() => pickStart(b.start.id)}
                    style={{ minHeight: 32, padding: "4px 0", borderRadius: 6, justifyContent: "space-between" }}
                  >
                    <span style={{ fontWeight: 600, fontSize: 14 }}>
                      {b.start.name}
                      {b.start.direction && <span style={{ color: "var(--text-muted)", fontWeight: 500 }}> · to the {b.start.direction}</span>}
                    </span>
                    <span className="tnum" style={{ font: "700 18px var(--font-display)" }}>
                      {n}
                    </span>
                  </button>
                </div>
              );
            })}
          </>
        ) : null}
      </div>

      {(rank?.gated || origin) && remainingQ.data && (
        <div style={{ marginTop: "auto", padding: "14px 20px", borderTop: "1px solid var(--border-subtle)", fontSize: 14, color: "var(--text-secondary)" }}>
          <b style={{ color: "var(--text-primary)" }}>{leftCount.toLocaleString("en-GB")}</b> {plural(leftCount, "hill", "hills")} left
          on your lists{origin ? ` within ${fmtKm(origin.radiusKm)} km` : ""}. The map shows {origin ? "them" : "them all"}.
        </div>
      )}
    </>
  );

  // --- panel: state B ---
  const startName = detail?.start.name ?? rank?.starts.find((s) => s.id === startId)?.name;
  const startKind = detail?.start.kind ?? rank?.starts.find((s) => s.id === startId)?.kind;
  const kindLabel = startKind === "station" ? "Station" : "Car park";
  const eyebrowParts = detail
    ? [startKind ? kindLabel : null, detail.start.placeName, detail.start.operator].filter(Boolean)
    : [];

  const listNames = detail?.lists.map((l) => l.name) ?? [];
  const codes = listCodes(listNames);
  const perList = listNames.map((name, i) => ({ name, n: insideHills.filter((h) => h.c.includes(i)).length }));

  const bands = detail?.bands ?? [];
  const bandCounts = bands.map((b, i) =>
    i === bands.length - 1 ? insideHills.length : insideHills.filter((h) => h.distanceM <= b.edgeM).length,
  );

  const runner = detail?.runnerUp ?? null;
  const best = detail?.best ?? null;
  const nearRunner = runner != null && bearing != null && angularDifference(bearing, runner.bearing) < 11.25;
  const swing = nearRunner && best ? best : runner;

  const outsideHills = hills.filter((h) => !h.inside);

  const hillRow = (h: WhereToStartHill, muted?: boolean) => (
    <div key={h.id} className="wts-hill" style={{ opacity: muted ? 0.6 : 1 }}>
      <Link
        to="/peaks/$slug"
        params={{ slug: h.slug }}
        style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600, color: "var(--text-primary)", textDecoration: "none" }}
      >
        {h.name}
      </Link>
      <span className="wts-tag">{h.c.map((i) => codes[i] ?? "").join("+")}</span>
      <span className="mono" style={{ fontSize: 10, letterSpacing: ".04em", minWidth: 62, textAlign: "right", color: "var(--text-muted)", textTransform: "uppercase" }}>
        {km(h.distanceM)} km {h.direction}
      </span>
    </div>
  );

  const stateB = (
    <>
      <div style={{ padding: "20px 20px 16px", display: "flex", flexDirection: "column", gap: 12, borderBottom: "1px solid var(--border-subtle)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <button
            type="button"
            className="wts-link"
            onClick={() => update({ start: undefined, bearing: undefined })}
            style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, minHeight: 32 }}
          >
            <ChevronLeft size={16} aria-hidden="true" />
            All start points
          </button>
          {helpButton}
        </div>
        {listsBlock(true)}
        {dayBlock(true)}
      </div>

      {detailGated ? (
        <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
          <h1 style={{ margin: 0, font: "600 24px var(--font-display)" }}>{startName ?? place ?? "This start"}</h1>
          <div style={{ padding: 18, borderRadius: 12, background: "var(--surface-card)", border: "1px solid var(--border-default)", display: "flex", flexDirection: "column", gap: 14 }}>
            <span style={{ fontSize: 14, lineHeight: 1.5, color: "var(--text-secondary)" }}>
              Pro shows every start. Free shows the best one for your lists.
            </span>
            <Button block onClick={() => void navigate({ to: "/plan" })}>
              See all with Pro
            </Button>
          </div>
        </div>
      ) : detailQ.isError ? (
        <div style={{ padding: 20 }}>
          <EmptyState
            icon={<Compass size={20} />}
            title={
              detailQ.error instanceof ApiError && detailQ.error.status === 404
                ? "That start isn't in reach of these lists."
                : errorCopy(detailQ.error)
            }
            action={
              detailQ.error instanceof ApiError && detailQ.error.status === 404
                ? { label: "All start points", onClick: () => update({ start: undefined }) }
                : { label: "Try again", onClick: () => void detailQ.refetch() }
            }
          />
        </div>
      ) : !detail ? (
        <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
          <Skel width="60%" height={14} />
          <Skel width="80%" height={28} />
          <Skel height={64} />
          <Skel height={120} />
        </div>
      ) : (
        <>
          <div style={{ padding: "18px 20px 14px", display: "flex", flexDirection: "column", gap: 12, borderBottom: "1px solid var(--border-subtle)" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="eyebrow" style={{ fontSize: 10 }}>
                {eyebrowParts.join(" · ")}
              </span>
              <h1 style={{ margin: 0, font: "600 24px var(--font-display)" }}>{detail.start.name}</h1>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <span className="tnum" aria-live="polite" style={{ font: "700 40px/1 var(--font-display)", color: "var(--text-accent)" }}>
                {insideHills.length}
              </span>
              <span style={{ fontSize: 15, color: "var(--text-secondary)" }}>
                {plural(insideHills.length, "hill", "hills")} you haven't collected,{" "}
                <b style={{ color: "var(--text-primary)" }}>
                  {detailMode === "centred" || bearing == null ? `within ${km(radiusM)} km` : compass8Long(bearing)}
                </b>
              </span>
            </div>
            {bands.length === 3 && (
              <div
                role="img"
                aria-label={`Distance from the ${kindLabel.toLowerCase()}: ${bands
                  .map((b, i) => `${bandCounts[i]} within ${km(b.edgeM)} km`)
                  .join(", ")}`}
                style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 6 }}
              >
                {bands.map((b, i) => (
                  <span
                    key={b.edgeM}
                    aria-hidden="true"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      padding: "8px 10px",
                      borderRadius: 8,
                      background: "var(--surface-sunken)",
                      border: "1px solid var(--border-subtle)",
                    }}
                  >
                    <b className="tnum" style={{ font: "700 18px var(--font-display)", color: "var(--text-primary)" }}>
                      {bandCounts[i]}
                    </b>
                    <span style={{ fontSize: 11, color: "var(--text-secondary)" }}>within {km(b.edgeM)} km</span>
                  </span>
                ))}
              </div>
            )}
            <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
              {perList.map((p) => `${p.n} ${p.name}`).join(" · ")}.
              {perList.length > 1 && " Hills on two lists count once."}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {detailMode === "lobe" && swing && (
                <button
                  type="button"
                  onClick={() => {
                    setLiveBearing(swing.bearing);
                    onBearingCommit(swing.bearing);
                  }}
                  style={{
                    height: 36,
                    padding: "0 12px",
                    borderRadius: 8,
                    border: "1px solid var(--border-strong)",
                    background: "var(--surface-raised)",
                    color: "var(--text-primary)",
                    font: "600 13px var(--font-sans)",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                  }}
                >
                  <RotateCcw size={14} aria-hidden="true" />
                  {nearRunner && best
                    ? `Back to ${best.count} to the ${compass16(best.bearing)}`
                    : runner
                      ? `Or ${runner.count} to the ${runner.direction}`
                      : ""}
                </button>
              )}
              <div style={{ marginLeft: "auto" }}>
                <Segmented
                  label="Shape"
                  value={mode}
                  options={[
                    { value: "lobe", label: "One side" },
                    { value: "centred", label: "Centred" },
                  ]}
                  onChange={setMode}
                />
              </div>
            </div>
          </div>

          <div style={{ padding: "12px 20px", display: "flex", flexDirection: "column" }}>
            {insideHills.map((h) => hillRow(h))}
            {outsideHills.length > 0 && (
              <>
                <div className="eyebrow" style={{ fontSize: 10, padding: "14px 0 4px" }}>
                  Further round
                </div>
                {outsideHills.map((h) => hillRow(h, true))}
              </>
            )}
            {hills.length === 0 && (
              <p style={{ margin: 0, fontSize: 14, color: "var(--text-secondary)" }}>No hills left within reach of this start.</p>
            )}
            {detail.start.alternatives.length > 0 && (
              // D27: equivalent starts, plain names with no counts so this
              // never reads as a ranking of its own.
              <details className="wts-alts">
                <summary>{detail.start.kind === "station" ? "Other stations here" : "Other car parks here"}</summary>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", paddingTop: 8, fontSize: 13 }}>
                  {detail.start.alternatives.map((a) => (
                    <button key={a.id} type="button" className="wts-link" style={{ fontWeight: 500 }} onClick={() => pickStart(a.id)}>
                      {a.name}
                    </button>
                  ))}
                </div>
              </details>
            )}
          </div>
          <div
            style={{
              marginTop: "auto",
              padding: "14px 20px",
              borderTop: "1px solid var(--border-subtle)",
              display: "flex",
              flexDirection: "column",
              gap: 6,
              fontSize: 14,
            }}
          >
            <span style={{ color: "var(--text-secondary)" }}>Draw the route in your usual app, then:</span>
            <Link to="/route-analyser" className="wts-link">
              Check what your route collects
            </Link>
          </div>
        </>
      )}
    </>
  );

  // --- legend ---
  const topName = rank?.lists[0]?.name ?? remainingQ.data?.lists[0]?.name;
  const multi = (remainingQ.data?.lists.length ?? 0) > 1;
  const showRegions = !!rank && rank.regions.length > 0 && !startId;
  const dot = (size: number, filled: boolean, op = 1, color = "var(--accent)") => (
    <span
      aria-hidden="true"
      style={{ width: size, height: size, borderRadius: "50%", boxSizing: "border-box", background: filled ? color : "transparent", border: filled ? 0 : `2.5px solid ${color}`, opacity: op, flex: "none" }}
    />
  );
  const berthItem = origin ? (
    <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
      <span aria-hidden="true" style={{ width: 14, height: 14, borderRadius: "50%", border: "1.5px dotted var(--text-muted)", flex: "none" }} />
      Within {fmtKm(origin.radiusKm)} km of {place === AREA_LABEL ? "this area" : (place ?? "here")}
    </span>
  ) : null;
  const legend = (
    <div
      style={{
        position: "absolute",
        left: 16,
        bottom: 28,
        maxWidth: "calc(100% - 32px)",
        display: "flex",
        gap: "6px 16px",
        alignItems: "center",
        flexWrap: "wrap",
        padding: "10px 14px",
        borderRadius: 10,
        background: "var(--surface-overlay)",
        border: "1px solid var(--border-default)",
        backdropFilter: "blur(6px)",
        fontSize: 12,
        color: "var(--text-secondary)",
        zIndex: 2,
      }}
    >
      {startId && picked ? (
        <>
          <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {dot(11, true)}
            {multi && topName ? `On ${topName}` : "Remaining on your lists"}
          </span>
          {multi && (
            <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {dot(11, false)}
              Other lists only
            </span>
          )}
          <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {dot(7, true, 0.7, "var(--status-success)")}
            Collected
          </span>
          {berthItem}
          <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span aria-hidden="true" style={{ width: 14, height: 14, borderRadius: "50%", border: "1.5px dashed var(--accent)", flex: "none" }} />
            {picked.mode === "lobe" ? `Area for a ${loop} km loop` : `Within ${km(picked.radiusM)} km`}
          </span>
          {rank && rank.starts.length > 1 && (
            <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span aria-hidden="true" style={{ font: "700 11px var(--font-display)", color: "var(--text-primary)", border: "1px solid var(--border-strong)", borderRadius: 5, padding: "1px 5px" }}>
                {rank.starts[1]?.count ?? 0}
              </span>
              Other starts, best side
            </span>
          )}
        </>
      ) : (
        <>
          <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {dot(8, true)}
            Remaining on your lists
          </span>
          <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {dot(6, true, 0.7, "var(--status-success)")}
            Collected
          </span>
          {berthItem}
          {showRegions && <span className="wts-legend-hint">Zoom in or pick a start to see each car park and station.</span>}
        </>
      )}
    </div>
  );

  return (
    <div className="wts-root">
      <style>{CSS}</style>
      <aside className="wts-panel" aria-label="Where to start">
        {startId ? stateB : stateA}
      </aside>
      <WhereToStartIntro open={introOpen} onClose={closeIntro} />
      <section className="wts-map" aria-label="Map">
        <WhereToStartMap
          remaining={haveLists ? remainingQ.data : undefined}
          starts={rank?.starts ?? []}
          regions={rank?.regions ?? []}
          origin={origin}
          picked={picked}
          fit={fit}
          onPickStart={pickStart}
          onSearchArea={searchArea}
          onBearingChange={onBearingChange}
          onBearingCommit={onBearingCommit}
        >
          {legend}
        </WhereToStartMap>
      </section>
    </div>
  );
}

function RowSkeletons() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "0 4px" }} aria-busy="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <Skel key={i} height={56} radius="12px" />
      ))}
    </div>
  );
}
